import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { tradeStore } from '@/models/stores';
import type { Auction, Trade } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const useUpdateTradeAuctionsMutation = (tradeId: Trade['id']) => {
  const queryClient = useQueryClient();

  const updateTradeAuctionsMutation = useMutation({
    onMutate: async ({ auctions }) => {
      // Optimistic update
      tradeStore.update({
        id: tradeId,
        trade: { auctions },
      });
    },
    mutationFn: async ({ auctions }: { auctions: Auction['id'][] }) => {
      const response = await pdkAxios
        .post(`/trades/${tradeId}/auctions`, { auctions })
        .catch((e) => {
          console.error('Error updating trade auctions', e);
          throw e;
        });

      return response.data;
    },
    onError: (error, variables) => {
      console.error('Error updating trade auctions', error, variables);

      // Revert optimistic update by refetching from store
      const trade = tradeStore.getById(tradeId);
      if (trade) {
        // This will trigger a re-render with the original data
        tradeStore.save(trade);
      }

      toast({
        title: 'Error',
        description: 'Failed to update auctions. Please try again.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['trade', tradeId] });
    },
  });

  return { updateTradeAuctionsMutation };
};
